import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { ArrivalIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'

import { API } from '../../../../../api/apis.config'
import { createArrivalAndDeparture } from '../../../../../api/arrivals-and-departures/create-arrival-and-departure'
import { updateArrivalAndDeparture } from '../../../../../api/arrivals-and-departures/update-arrival-and-departure'
import { useModalManager } from '../../../../../lib/hooks/use-modal-manager'
import { CurrencyEnum } from '../../../../../lib/types/enums/common/currency.enum'
import {
  ArrivalAndDeparture,
  ArrivalAndDepartureFilters
} from '../../../../../lib/types/models/arrival-and-departure/arrival-and-departure'
import { modalButtonConfig } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../../../elements/dynamic-table'
import NestedDataTable from '../../../../elements/nested-table'
import ArrivalAndDepartureForm from './arrival-and-departure-form'

type Props = {
  coachCompanyId?: number
  currency?: CurrencyEnum
  country: string
}

const ArrivalAndDepartureTable = ({ currency, coachCompanyId, country }: Props) => {
  const tableref = useRef<DynamicTableRef<ArrivalAndDeparture>>(null)

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    ArrivalAndDeparture
  >(['create', 'edit'])

  const {
    mutateAsync: createArrivalAndDepartureMutation,
    isPending: isCreateArrivalAndDeparturePending
  } = useMutation({
    mutationFn: createArrivalAndDeparture,
    onError: showAxiosError,
    onSuccess: () => {
      tableref.current?.refetch()
      closeModel('create')
    }
  })

  const {
    mutateAsync: updateArrivalAndDepartureMutation,
    isPending: isUpdateCulturalOutingPending
  } = useMutation({
    mutationFn: updateArrivalAndDeparture,
    onError: showAxiosError,
    onSuccess: () => {
      tableref.current?.refetch()
      closeModel('edit')
    }
  })

  const columns: Column<ArrivalAndDeparture>[] = [
    {
      accessorKey: 'fromTo',
      header: 'From/To',
      enableSorting: true,
      cell: ({ row }) => <Badge variant="white" text={row.original.fromTo} />
    },
    {
      accessorKey: 'numberOfSeats',
      header: 'Seats',
      enableSorting: true
    },
    {
      accessorKey: 'comment',
      header: 'Comments',
      enableSorting: true,
      cell: ({ row }) => row.original.comment || '-'
    },
    {
      accessorKey: 'cost',
      header: 'Cost A+D',
      enableSorting: true,
      cell: ({ row }) => {
        const cost = NumberFormatter.formatCurrency(row.original.cost, currency)
        return <Badge variant="primary" text={cost} />
      }
    },
    {
      accessorKey: 'validUntil',
      header: 'Valid until',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          text={row.original.validUntil ? DateFormatter.DDMMYYYY(row.original.validUntil) : '-'}
        />
      )
    }
  ]

  return (
    <>
      <NestedDataTable<ArrivalAndDeparture, ArrivalAndDepartureFilters>
        ref={tableref}
        overflow
        variant="action"
        title={'Arrivals & Departures'}
        deleteMessage={'Are you sure you want to delete this ?'}
        actions={[
          {
            icon: PlusIcon,
            variant: 'secondary',
            label: 'Add arrival / departure transfer',
            fn: () => toggleModal('create')
          }
        ]}
        columns={columns}
        onEditAction={(data) => {
          toggleModal('edit', data)
        }}
        endpoint={API.arrivalAndDeparture.getAll.url}
        filters={useMemo(() => ({ coachCompanyId }), [coachCompanyId])}
      />

      <Modal
        isOpen={modalState.create}
        title={'Arrivals & Departures'}
        icon={ArrivalIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <ArrivalAndDepartureForm
          country={country}
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              await createArrivalAndDepartureMutation({ coachCompanyId, ...values })
            },
            isLoading: isCreateArrivalAndDeparturePending,
            isEnglish: true,
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Arrivals & Departures'}
          icon={ArrivalIcon}
          hideActions
          size="medium"
          onCancel={() => closeModel('edit')}
        >
          <ArrivalAndDepartureForm
            country={country}
            className={MODAL_CONTENT_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onConfirm: async (values) => {
                await updateArrivalAndDepartureMutation({
                  id: selectedData.id,
                  coachCompanyId,
                  ...values
                })
              },
              isLoading: isUpdateCulturalOutingPending,
              isEnglish: true,
              onCancel: () => closeModel('edit')
            })}
          />
        </Modal>
      )}
    </>
  )
}

export default ArrivalAndDepartureTable
